import { useState, useMemo } from "react";
import { FaCheck } from "react-icons/fa";

export default function LoanCalculator() {
  const [loanAmount, setLoanAmount] = useState(250000);
  const [loanTerm, setLoanTerm] = useState(30);
  const [interestRate, setInterestRate] = useState(1.5);

  const { totalPayable, apr } = useMemo(() => {
    const dailyInterest = (loanAmount * interestRate) / 100;
    const totalInterest = dailyInterest * loanTerm;
    const totalPayable = loanAmount + totalInterest;
    const apr = interestRate * 365;
    return { totalPayable, apr };
  }, [loanAmount, interestRate, loanTerm]);

  return (
    <section
      id="loan-calculator"
      className="w-full pt-16 pb-28 sm:pt-20 sm:pb-36 px-6 lg:px-20 font-sans relative overflow-hidden"
      style={{
        backgroundColor: '#D0E9FFCF',
        clipPath: 'polygon(0 0, 100% 0, 100% 92%, 0 100%)'
      }}
    >
      <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center relative z-10">

        {/* Left Side: Sliders */}
        <div className="lg:col-span-7 flex flex-col gap-6 w-full text-left">

          <div className="flex flex-col gap-2 border-b border-[#12141D]/10 pb-5 text-center md:text-left items-center md:items-start">
            <h2 className="text-[30px] sm:text-4xl md:text-[42px] font-semibold text-[#0F172A] tracking-tight leading-tight whitespace-nowrap">
              Smart Loan Calculator
            </h2>

            <p className="text-slate-500 font-medium text-xs leading-relaxed max-w-xl">
              Plan your finances better by calculating your monthly EMI and total outflow.
            </p>
          </div>

          <div className="h-[1px] w-full bg-slate-200/80 my-2" />

          {/* Sliders Container */}
          <div className="flex flex-col gap-8 w-full">

            {/* Slider 1: Loan Amount */}
            <Slider
              label="Loan Amount"
              value={loanAmount}
              min={10000}
              max={500000}
              step={5000}
              onChange={setLoanAmount}
              displayValue={`₹${loanAmount.toLocaleString('en-IN')}`}
              minLabel="₹10,000"
              maxLabel="₹5,00,000"
            />

            {/* Slider 2: Tenure */}
            <Slider
              label="Tenure (Days)"
              value={loanTerm}
              min={10}
              max={30}
              step={1}
              onChange={setLoanTerm}
              displayValue={`${loanTerm} Days`}
              minLabel="10 days"
              maxLabel="30 days"
            />

            {/* Slider 3: Interest Rate */}
            <Slider
              label="Interest Rate (% p.d.)"
              value={interestRate}
              min={0.5}
              max={1.5}
              step={0.1}
              onChange={setInterestRate}
              displayValue={`${interestRate.toFixed(1)}%`}
              minLabel="0.5%"
              maxLabel="1.5%"
            />

          </div>

        </div>

        {/* Right Side: Result Summary Card */}
        <div className="lg:col-span-5 w-full">
          <div className="bg-[#0B1220] rounded-[18px] md:rounded-[28px] px-3.5 py-3 md:p-7 flex flex-col gap-2.5 relative overflow-hidden shadow-[0_20px_50px_rgba(11,18,32,0.25)] text-left max-w-[320px] md:max-w-full mx-auto">


            {/* Calculations Fields */}
            <div className="flex flex-col relative z-10 gap-0.5">
              {[
                { label: "Loan Amount", value: `₹ ${loanAmount.toLocaleString('en-IN')}` },
                { label: "Tenure", value: `${loanTerm} Days` },
                { label: "Interest Rate", value: `${interestRate.toFixed(1)}%` },
                { label: "APR", value: `${apr.toFixed(1)}%` },
              ].map((item) => (
                <div
                  key={item.label}
                  className="flex justify-between items-center border-b border-white/5 py-1"
                >
                  <span className="text-slate-400 text-[9px] md:text-[14px] font-medium">
                    {item.label}
                  </span>

                  <span className="text-white text-[9px] md:text-[14px] font-bold">
                    {item.value}
                  </span>
                </div>
              ))}

              {/* Total Payable */}
              <div className="flex justify-between items-center pt-1">
                <span className="text-slate-300 text-[11px] md:text-[15px] font-bold">
                  Total Payable
                </span>

                <span className="text-[var(--primary)] text-[15px] md:text-xl font-black">
                  ₹ {totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                </span>
              </div>
            </div>

            {/* CTA Button */}
            <a
              href={import.meta.env.VITE_WEBSITE_LINK}
              target="_blank"
              className="w-full py-1.5 md:py-3 bg-white hover:bg-slate-50 text-[var(--primary)] font-bold text-center rounded-[7px] md:rounded-[12px] transition-all active:scale-95 shadow-md relative z-10 block text-[11px] md:text-[15px]"
            >
              Get Started
            </a>

            {/* Rate & Charges Section */}
            <div className="flex flex-row gap-1 relative z-10 border-t border-white/10 pt-2 ">
            <div>

       
              <h4 className="text-white text-[12px] md:text-[16px] font-bold">
                Rate & Charges
              </h4>

              <div className="flex flex-col gap-0.5">
                {[
                  `Loan Amount: ₹5,000 to ₹5,00,000`,
                  "Tenure: 10 to 30 Days",
                  "No Pre-clousre Charges",
                  "No Prepayment Charges",
                  "APR calculated by formula",
                  "Net Monthly Salary > ₹35,000"
                ].map((charge, idx) => (
                  <div key={idx} className="flex items-center gap-1">
                    <div className="flex-shrink-0 flex items-center justify-center w-3 h-3 rounded-full border border-white/20">
                      <FaCheck className="text-[4px] text-white" />
                    </div>

                    <span className="text-[#94A3B8] text-[8px] md:text-[13px] font-medium leading-tight">
                      {charge}
                    </span>
                  </div>
                ))}
              </div>
                   </div>
              <svg width="85" height="127" viewBox="0 0 85 127" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M25.395 67.7199C36.4841 67.7199 45.8802 60.694 49.3509 50.7899H0L8.46499 33.86H49.3509C45.8802 24.0406 36.4841 16.93 25.395 16.93H0L8.46499 0H84.6499L76.1849 16.93H59.2549C62.9795 21.8397 65.6037 27.5959 66.8734 33.86H84.6499L76.1849 50.7899H66.8734C65.0606 59.587 60.5017 67.5813 53.8536 73.6209C47.2055 79.6605 38.8115 83.4334 29.8814 84.396L84.6499 126.975H55.0224L0 84.6499V67.7199H25.395Z" fill="white" fill-opacity="0.05"/>
</svg>

            </div>

          </div>
        </div>

      </div>
    </section>
  );
}

// Custom Slider Sub-component
const Slider = ({ label, value, onChange, min, max, step, displayValue, minLabel, maxLabel }: any) => {
  const percentage = ((value - min) / (max - min)) * 100;

  return (
    <div className="flex flex-col w-full text-left gap-2">
      {/* Header Info */}
      <div className="flex justify-between items-baseline mb-1">
        <span className="text-[#0F172A] font-extrabold text-[16px] tracking-tight">{label}</span>
        <span className="text-[#41A655] font-black text-lg">{displayValue}</span>
      </div>

      {/* Input Slider Element with custom knob styling */}
      <div className="relative w-full flex items-center h-4">
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          className="w-full h-1.5 rounded-full appearance-none cursor-pointer outline-none bg-slate-200 accent-[#41A655] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#41A655] [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md"
          style={{
            background: `linear-gradient(to right, #41A655 ${percentage}%, #E2E8F0 ${percentage}%)`,
          }}
        />
      </div>

      {/* Range Labels */}
      <div className="flex justify-between text-[11px] font-bold text-slate-400">
        <span>{minLabel}</span>
        <span>{maxLabel}</span>
      </div>
    </div>
  );
};
